import { Controller } from "@hotwired/stimulus"
import mapboxManager from "../../lib/mapbox_manager"

const MAP_ID = "directories-show-map"
const DATA_SCRIPT_ID = "directory-show-map-data"

// Directory landing page map: lazy-loads Mapbox with office pins.
export default class extends Controller {
  static values = {
    zoom: Number,
    accessToken: String,
  }

  connect() {
    this.abortController = new AbortController()
    this.initialize()
  }

  disconnect() {
    this.abortController?.abort()
    this.teardown()
  }

  initialize() {
    if (!this.accessTokenValue) {
      console.error("Mapbox access token not provided")
      return
    }

    const mapOptions = this.prepareMapOptions()
    if (!mapOptions) return

    mapboxManager.registerLazyMap(this.element, mapOptions, MAP_ID, (map) => {
      this.setupMarkers(map)
    }, this.accessTokenValue)
  }

  teardown() {
    if (mapboxManager.intersectionObserver) {
      mapboxManager.intersectionObserver.unobserve(this.element)
    }
    mapboxManager.lazyMaps?.delete(this.element)
    mapboxManager.destroyMap(MAP_ID)
  }

  readOfficesData() {
    const el = document.getElementById(DATA_SCRIPT_ID)
    if (!el?.textContent?.trim()) return []
    try {
      return JSON.parse(el.textContent)
    } catch {
      return []
    }
  }

  prepareMapOptions() {
    const offices = this.readOfficesData()
    const firstOfficeWithLocation = offices.find((office) => office.latitude && office.longitude)
    if (!firstOfficeWithLocation) {
      console.warn("No offices with location data found")
      return null
    }

    return {
      style: "mapbox://styles/mapbox/light-v11",
      center: [
        parseFloat(firstOfficeWithLocation.longitude),
        parseFloat(firstOfficeWithLocation.latitude),
      ],
      zoom: this.zoomValue,
    }
  }

  setupMarkers(map) {
    const offices = this.readOfficesData()
    if (!offices.length) return

    this.setupMapboxMarkers(map, offices)
  }

  setupMapboxMarkers(map, offices) {
    map.on("load", () => {
      const markers = []

      for (const office of offices) {
        if (!office.latitude || !office.longitude) continue

        const el = document.createElement("div")
        el.className = "mapbox-marker"
        el.style.width = "32px"
        el.style.height = "32px"
        el.style.cursor = "pointer"

        const img = document.createElement("img")
        img.src = "/map-pin.png"
        img.style.width = "100%"
        img.style.height = "100%"
        img.style.display = "block"
        img.alt = office.name || "Office location"
        el.appendChild(img)

        const marker = mapboxManager.createMarker({ element: el, anchor: "bottom" })
        marker.setLngLat([parseFloat(office.longitude), parseFloat(office.latitude)])
        marker.addTo(map)
        markers.push(marker)
      }

      const existingMapData = mapboxManager.activeMaps.get(MAP_ID)
      if (existingMapData) {
        existingMapData.markers = markers
      }
    })
  }
}
